import React from 'react';
import { Helmet } from 'react-helmet-async';

// Utility functions for SEO optimization
const generateKeywords = (sound) => {
  if (!sound) return 'sons gratuits, libre de droits, MP3 gratuit, WAV gratuit, bruitages gratuits, effets sonores gratuits, téléchargement audio gratuit, fichiers audio libres, sound effects free, royalty free sounds';
  
  const baseKeywords = [
    sound.title,
    sound.theme,
    `${sound.title} gratuit`,
    `${sound.title} libre de droits`,
    `${sound.title} MP3 gratuit`,
    `${sound.title} WAV gratuit`,
    `${sound.title} téléchargement gratuit`,
    `son ${sound.theme} gratuit`,
    `effet sonore ${sound.theme} gratuit`,
    `bruit ${sound.theme} gratuit`,
    `bruitage ${sound.theme} gratuit`,
    `ambiance ${sound.theme} gratuite`,
    `${sound.theme} libre de droits`,
    `${sound.theme} MP3`,
    `${sound.theme} WAV`,
    `fichier audio ${sound.theme}`,
    `sound effect ${sound.theme}`,
    `royalty free ${sound.theme}`
  ];
  
  // Add usage-based keywords
  const usageKeywords = [
    `${sound.title} montage vidéo`,
    `${sound.title} podcast gratuit`,
    `${sound.title} jeu vidéo`,
    `${sound.title} film`,
    `${sound.title} animation`,
    `${sound.title} projet créatif`,
    `${sound.title} usage commercial`,
    `${sound.title} sans copyright`,
    `${sound.title} creative commons`
  ];
  
  return [...baseKeywords, ...usageKeywords].join(', ');
};

const generateRichDescription = (sound) => {
  if (!sound) return 'Découvrez notre collection de sons gratuits et libres de droits. Téléchargez des effets sonores de qualité professionnelle pour vos projets créatifs.';
  
  const usageCases = {
    'swoosh': 'parfait pour les transitions vidéo, animations et effets de mouvement',
    'explosion': 'idéal pour les jeux vidéo, films d\'action et effets dramatiques',
    'nature': 'excellent pour la relaxation, méditation et ambiances apaisantes',
    'ambiance': 'parfait pour créer des atmosphères immersives dans vos projets',
    'cartoon': 'idéal pour les animations, dessins animés et contenus humoristiques',
    'impact': 'parfait pour les effets de choc, collisions et impacts dramatiques'
  };
  
  const usage = usageCases[sound.theme] || 'parfait pour enrichir vos créations audio et vidéo';
  
  return `Téléchargez gratuitement "${sound.title}" - un son de qualité professionnelle de la catégorie ${sound.theme}. Ce fichier audio libre de droits est ${usage}. Disponible en formats MP3 et WAV haute qualité. Durée: ${sound.duration}. Utilisable dans vos projets personnels et commerciaux sans restriction.`;
};

// Fonction pour convertir la durée en format ISO 8601
const convertDurationToISO8601 = (duration) => {
  if (!duration) return 'PT0S';
  
  // Si la durée est déjà au format MM:SS ou HH:MM:SS
  const parts = duration.split(':');
  let totalSeconds = 0;
  
  if (parts.length === 2) {
    // Format MM:SS
    totalSeconds = parseInt(parts[0]) * 60 + parseInt(parts[1]);
  } else if (parts.length === 3) {
    // Format HH:MM:SS
    totalSeconds = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
  } else {
    // Essayer de parser comme nombre de secondes
    totalSeconds = parseInt(duration) || 0;
  }
  
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  let iso8601 = 'PT';
  if (hours > 0) iso8601 += `${hours}H`;
  if (minutes > 0) iso8601 += `${minutes}M`;
  if (seconds > 0 || (hours === 0 && minutes === 0)) iso8601 += `${seconds}S`;
  
  return iso8601;
};

const generateStructuredData = (sound, currentUrl) => {
  const baseUrl = 'https://sound-nest.vercel.app';
  
  if (sound) {
    // Date de publication simulée (peut être remplacée par une vraie date si disponible)
    const publishDate = sound.datePublished || '2024-01-01';
    
    return {
      '@context': 'https://schema.org',
      '@type': 'AudioObject',
      'name': sound.title,
      'description': generateRichDescription(sound),
      'url': currentUrl,
      'contentUrl': sound.mp3Url || sound.downloadUrl,
      'encodingFormat': sound.mp3Url && sound.wavUrl ? ['audio/mpeg', 'audio/wav'] : 
                       sound.mp3Url ? 'audio/mpeg' : 'audio/wav',
      'duration': convertDurationToISO8601(sound.duration),
      'genre': sound.theme,
      'datePublished': publishDate,
      'dateModified': new Date().toISOString().split('T')[0],
      'inLanguage': 'fr-FR',
      'isAccessibleForFree': true,
      'license': 'https://creativecommons.org/publicdomain/zero/1.0/',
      'usageInfo': 'Libre de droits pour usage personnel et commercial',
      'keywords': generateKeywords(sound).split(', '),
      'thumbnailUrl': `${baseUrl}/logo192.png`,
      'creator': {
        '@type': 'Organization',
        'name': 'LaSonotheque.org',
        'url': 'https://lasonotheque.org'
      },
      'publisher': {
        '@type': 'Organization',
        'name': 'SoundNest',
        'url': baseUrl,
        'logo': {
          '@type': 'ImageObject',
          'url': `${baseUrl}/logo192.png`,
          'width': 192,
          'height': 192
        }
      },
      'copyrightHolder': {
        '@type': 'Organization',
        'name': 'LaSonotheque.org'
      },
      'mainEntity': {
        '@type': 'CreativeWork',
        'name': sound.title,
        'category': sound.theme,
        'genre': sound.theme
      },
      'offers': {
        '@type': 'Offer',
        'price': '0',
        'priceCurrency': 'EUR',
        'availability': 'https://schema.org/InStock',
        'seller': {
          '@type': 'Organization',
          'name': 'SoundNest'
        }
      }
    };
  }
  
  // Homepage structured data
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'SoundNest - Sons Gratuits et Libres de Droits',
    'url': baseUrl,
    'description': 'La plus grande collection de sons gratuits et libres de droits. Téléchargez des effets sonores de qualité professionnelle pour vos projets créatifs.',
    'publisher': {
      '@type': 'Organization',
      'name': 'SoundNest',
      'url': baseUrl
    },
    'potentialAction': {
      '@type': 'SearchAction',
      'target': `${baseUrl}/?search={search_term_string}`,
      'query-input': 'required name=search_term_string'
    },
    'mainEntity': {
      '@type': 'CollectionPage',
      'name': 'Collection de Sons Gratuits',
      'description': 'Plus de 600 sons gratuits organisés par catégories'
    }
  };
};

const SEO = ({ title, description, sound, currentUrl, breadcrumbs }) => {
  const siteTitle = 'SoundNest';
  const defaultDescription = 'Téléchargez gratuitement des sons libres de droits en MP3 et WAV. Bruitages, effets sonores, ambiances pour vos projets créatifs et professionnels. Fichiers audio gratuits sans restriction.';
  const baseUrl = (typeof window !== 'undefined' && window.location && window.location.origin)
    ? window.location.origin
    : (process.env.REACT_APP_SITE_URL || 'https://sound-nest.vercel.app');
  
  const pageTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const pageDescription = description || (sound ? generateRichDescription(sound) : defaultDescription);
  const pageUrl = currentUrl || baseUrl;
  const keywords = generateKeywords(sound);
  const structuredData = generateStructuredData(sound, pageUrl);

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="SoundNest" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <link rel="canonical" href={pageUrl} />
      <meta property="og:site_name" content={siteTitle} />
      
      {/* Language and locale */}
      <meta httpEquiv="content-language" content="fr-FR" />
      <meta name="language" content="French" />
      
      {/* Open Graph */}
      <meta property="og:type" content={sound ? 'music.song' : 'website'} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="SoundNest" />
      <meta property="og:image" content={`${baseUrl}/logo512.png`} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:image:alt" content={`SoundNest - ${sound ? sound.title : 'Sons Gratuits'}`} />
      <meta property="og:locale" content="fr_FR" />
      {sound && (
        <>
          <meta property="music:duration" content={sound.duration} />
          <meta property="music:musician" content="SoundNest" />
          <meta property="og:audio" content={sound.mp3Url || sound.downloadUrl} />
          <meta property="og:audio:type" content="audio/mpeg" />
        </>
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={`${baseUrl}/logo512.png`} />
      <meta name="twitter:image:alt" content={`SoundNest - ${sound ? sound.title : 'Sons Gratuits'}`} />
      <meta name="twitter:site" content="@SoundNest" />
      <meta name="twitter:creator" content="@SoundNest" />
      {sound && (
        <>
          <meta name="twitter:player" content={sound.mp3Url || sound.downloadUrl} />
          <meta name="twitter:player:width" content="320" />
          <meta name="twitter:player:height" content="180" />
        </>
      )}
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData, null, 2)}
      </script>
      
      {/* Breadcrumb structured data */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            'itemListElement': breadcrumbs.map((crumb, index) => ({
              '@type': 'ListItem',
              'position': index + 1,
              'name': crumb.name,
              'item': crumb.url
            }))
          })}
        </script>
      )}
      
      {/* Additional meta tags for better indexing */}
      <meta name="theme-color" content="#6366f1" />
      <meta name="msapplication-TileColor" content="#6366f1" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="format-detection" content="telephone=no" />
    </Helmet>
  );
};

export default SEO;
